import React, { useEffect, useState, useRef } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import usePrivateLink from "../config/usePrivateLink";

import { imgBaseUrl } from "../../api/api";

import { BatchStudent } from "../../api/StudentServices";

import logoutUser from "../../utils/logout";
import { createPortal } from "react-dom";
import {
  getManagementForPath,
  getManagementTabs,
  isTabActive,
} from "../master/scaiManagementConfig";

// Uses the same catalog as the sidebar, with no hardcoded module/tab arrays.
function ManagementNavbarTabs({ pathname, hasAccess }) {
  const management = getManagementForPath(pathname);
  const [openMenu, setOpenMenu] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ top: 76, left: 12 });
  const tabsRef = useRef(null);
  const popupRef = useRef(null);
  const visibleTabs = management
    ? getManagementTabs(management.key, hasAccess)
    : [];

  useEffect(() => setOpenMenu(null), [pathname]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const close = (event) => {
      if (
        !popupRef.current?.contains(event.target) &&
        !event.target.closest?.(".scai-config-tabs-dropdown")
      )
        setOpenMenu(null);
    };
    const onEscape = (event) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onEscape);
    };
  }, [openMenu]);

  useEffect(() => {
    const current = tabsRef.current?.querySelector('[aria-current="page"]');
    current?.scrollIntoView?.({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [pathname]);

  if (!management || !visibleTabs.length) return null;

  return (
    <>
      <div className="scai-config-tabs-shell">
        <div className="scai-config-tabs-brand" title={management.title}>
          <i className={management.icon || "ri-apps-line"} />
          <strong>{management.label || management.title}</strong>
        </div>
        <div
          className="scai-config-tabs-list"
          ref={tabsRef}
          role="navigation"
          aria-label={`${management.title} tabs`}
        >
          {visibleTabs.map((tab) => {
            const selected = isTabActive(tab, pathname);
            if (tab.subItems?.length) {
              return (
                <div className="scai-config-tabs-dropdown" key={tab.id}>
                  <button
                    type="button"
                    className={`scai-config-tab ${selected ? "active" : ""}`}
                    aria-expanded={openMenu === tab.id}
                    aria-current={selected ? "page" : undefined}
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      setPopoverPos({
                        top: rect.bottom + 9,
                        left: Math.max(
                          10,
                          Math.min(rect.left, window.innerWidth - 490),
                        ),
                      });
                      setOpenMenu((prev) => (prev === tab.id ? null : tab.id));
                    }}
                  >
                    <i className={tab.icon} />
                    <span>{tab.title || tab.label}</span>
                    <i className="ri-arrow-down-s-line" />
                  </button>
                  {openMenu === tab.id &&
                    createPortal(
                      <div
                        className="scai-config-tab-popover"
                        ref={popupRef}
                        style={{
                          position: "fixed",
                          top: popoverPos.top,
                          left: popoverPos.left,
                          right: "auto",
                        }}
                      >
                        {tab.subItems.map((child) => (
                          <Link
                            key={child.id || child.path}
                            to={child.path}
                            onClick={() => setOpenMenu(null)}
                            className={`scai-config-tab-option ${isTabActive(child, pathname) ? "active" : ""}`}
                          >
                            <i
                              className={child.icon || "ri-arrow-right-line"}
                            />
                            <span>
                              <strong>{child.title || child.label}</strong>
                              <small>{child.description}</small>
                            </span>
                          </Link>
                        ))}
                      </div>,
                      document.body,
                    )}
                </div>
              );
            }
            return (
              <Link
                key={tab.id || tab.path}
                to={tab.path}
                className={`scai-config-tab ${selected ? "active" : ""}`}
                aria-current={selected ? "page" : undefined}
              >
                <i className={tab.icon} />
                <span>{tab.title || tab.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default function Nav() {
  const hasAccess = usePrivateLink();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const initialTheme = localStorage.getItem("myTheme") === "true";

  const [theme, setTheme] = useState(initialTheme);

  const effectTrigger = useRef(false);

  const [studentSearch, setStudentSearch] = useState("");

  // Declare sessions before effects that reference it or include it in dependencies.
  const [sessions, setSessions] = useState([]);

  const [students, setStudents] = useState([]);

  const [showResult, setShowResult] = useState(false);
  const [showStudentSearchModal, setShowStudentSearchModal] = useState(false);
  const studentSearchInputRef = useRef(null);

  const handleStudentSearch = (value) => {
    setStudentSearch(value);
    if (value.trim().length < 2) {
      setStudents([]);
      setShowResult(false);
    } else {
      setShowResult(true);
    }
  };

  const changeTheme = () => {
    setTheme((prevTheme) => !prevTheme);

    effectTrigger.current = !effectTrigger.current;
  };

  useEffect(() => {
    const query = studentSearch.trim();
    if (query.length < 2) return undefined;

    let cancelled = false;
    const delay = setTimeout(async () => {
      try {
        const active = sessions?.find(
          (session) => Number(session.status) === 1,
        );
        const response = await BatchStudent(
          `search=${encodeURIComponent(query)}&session_id=${active?.id || ""}`,
        );
        if (!cancelled && response?.data?.status) {
          setStudents(
            Array.isArray(response.data.data) ? response.data.data : [],
          );
          setShowResult(true);
        }
      } catch (error) {
        if (!cancelled) {
          setStudents([]);
          console.error("Student search failed:", error);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(delay);
    };
  }, [studentSearch, sessions]);

  useEffect(() => {
    if (!showStudentSearchModal) return undefined;
    studentSearchInputRef.current?.focus();

    const handleSearchModalEscape = (event) => {
      if (event.key === "Escape") {
        setShowStudentSearchModal(false);
        setShowResult(false);
      }
    };

    document.addEventListener("keydown", handleSearchModalEscape);
    return () =>
      document.removeEventListener("keydown", handleSearchModalEscape);
  }, [showStudentSearchModal]);

  useEffect(() => {
    if (effectTrigger.current) {
      effectTrigger.current = !effectTrigger.current;

      localStorage.setItem("myTheme", theme);

      const coreCss = document.querySelector(".template-customizer-core-css");

      const themeCss = document.querySelector(".template-customizer-theme-css");

      if (theme) {
        document.documentElement.setAttribute("data-style", "dark");

        if (coreCss && themeCss) {
          coreCss.setAttribute("href", "/assets/vendor/css/rtl/core-dark.css");

          themeCss.setAttribute(
            "href",

            "/assets/vendor/css/rtl/theme-default-dark.css",
          );
        }
      } else {
        document.documentElement.setAttribute("data-style", "light");

        if (coreCss && themeCss) {
          coreCss.setAttribute("href", "/assets/vendor/css/rtl/core.css");

          themeCss.setAttribute(
            "href",

            "/assets/vendor/css/rtl/theme-default.css",
          );
        }
      }
    }
  }, [theme]);

  const [setting, setSetting] = useState(false);

  const [messageStatus, setMessageStatus] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setShowActionMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setShowActionMenu(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (
      setting?.message_status === "Yes" &&
      setting?.select_message_type === "Meta"
    ) {
      setMessageStatus(true);
    }
  }, [setting]);

  const handleClick = () => {
    if (
      setting?.message_status === "Yes" &&
      setting?.select_message_type === "Meta" &&
      hasAccess("/meta/wa_send_msg")
    ) {
      navigate("/meta/wa_send_msg");
    }

    // if (

    //   setting?.message_status === "Yes" &&

    //   setting?.select_message_type === "Meta" &&

    //   hasAccess("/meta/wa_send_msg")

    // ) {

    //   navigate("/meta/wa_send_msg");

    // }
  };

  useEffect(() => {
    setSetting(JSON.parse(localStorage.getItem("setting") ?? "{}"));

    setSessions(JSON.parse(localStorage.getItem("sessions") ?? "[]"));
  }, []);

  const activeSession = sessions?.find((s) => Number(s.status) === 1);

  const clearStudentSearch = () => {
    setStudentSearch("");
    setStudents([]);
    setShowResult(false);
  };

  const openStudent = (studentId) => {
    clearStudentSearch();
    navigate(`/student/details?id=${studentId}`);
  };

  return (
    <>
      <nav
        id="layout-navbar"
        className="layout-navbar navbar navbar-expand-xl scai-topbar"
      >
        <div className="scai-topbar-inner">
          {/* LEFT */}
          <div className="scai-topbar-left">
            <button
              type="button"
              className="scai-topbar-action scai-mobile-menu d-xl-none"
              onClick={() =>
                window.dispatchEvent(new Event("scai:toggle-mobile-sidebar"))
              }
              title="Open menu"
              aria-label="Open menu"
            >
              <i className="ri-menu-2-line" />
            </button>

            <button
              type="button"
              className="scai-topbar-action scai-back-action"
              onClick={() => navigate(-1)}
              title="Go back"
              aria-label="Go back"
            >
              <i className="ri-arrow-left-line" />
            </button>

            <ManagementNavbarTabs pathname={pathname} hasAccess={hasAccess} />
          </div>

          {/* STUDENT SEARCH LAUNCHER */}
          <div className="scai-global-search-launcher">
            <button
              type="button"
              className="scai-topbar-action scai-search-launcher"
              onClick={() => setShowStudentSearchModal(true)}
              title="Search students"
              aria-label="Search students"
            >
              <i className="ri-search-2-line" />
            </button>
          </div>

          {showStudentSearchModal &&
            createPortal(
              <div
                className="scai-search-modal-backdrop"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setShowStudentSearchModal(false);
                    setShowResult(false);
                  }
                }}
              >
                <section
                  className="scai-search-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="scai-search-modal-title"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <div className="scai-search-modal-heading">
                    <span className="scai-search-modal-symbol">
                      <i className="ri-user-search-line" />
                    </span>
                    <div>
                      <strong id="scai-search-modal-title">
                        Search students
                      </strong>
                      <small>
                        Find a student by name, father name, admission number or
                        mobile
                      </small>
                    </div>
                    <button
                      type="button"
                      className="scai-search-modal-close"
                      onClick={() => {
                        setShowStudentSearchModal(false);
                        setShowResult(false);
                      }}
                      aria-label="Close student search"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>

                  <div className="scai-search-modal-input-wrap">
                    <i className="ri-search-2-line" />
                    <input
                      ref={studentSearchInputRef}
                      type="text"
                      value={studentSearch}
                      placeholder="Type at least 2 characters…"
                      onChange={(event) =>
                        handleStudentSearch(event.target.value)
                      }
                      autoComplete="off"
                    />
                    {studentSearch && (
                      <button
                        type="button"
                        onClick={clearStudentSearch}
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <i className="ri-close-circle-line" />
                      </button>
                    )}
                  </div>

                  <div className="scai-search-modal-results-label">
                    {studentSearch.trim().length < 2
                      ? "RECENT / SEARCH"
                      : students.length
                        ? `${students.length} STUDENT${students.length === 1 ? "" : "S"} FOUND`
                        : "SEARCH RESULTS"}
                  </div>

                  <div className="scai-search-modal-results">
                    {studentSearch.trim().length < 2 ? (
                      <div className="scai-search-empty-state">
                        <span>
                          <i className="ri-search-line" />
                        </span>
                        <strong>Start typing to search</strong>
                        <small>
                          Search across student names, registration numbers and
                          mobile numbers.
                        </small>
                      </div>
                    ) : students.length > 0 ? (
                      students.map((stu) => {
                        const studentInitials = String(stu?.name || "?")
                          .trim()
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((part) => part?.[0]?.toUpperCase())
                          .join("");

                        return (
                          <button
                            type="button"
                            className="scai-student-result"
                            key={stu.id}
                            onClick={() => {
                              setShowStudentSearchModal(false);
                              openStudent(stu.id);
                            }}
                          >
                            <span className="scai-student-avatar">
                              {studentInitials || "S"}
                            </span>
                            <span className="scai-student-data">
                              {/* <strong>{stu?.name || "Student"}</strong> */}
                              <strong>
                                {stu?.name || "Student"}
                                {stu?.father_name && (
                                  <span className="scai-student-father">
                                    {" "}  S/o {stu.father_name}
                                  </span>
                                )}
                              </strong>
                              <small>
                                <span>
                                  <i className="ri-id-card-line" />
                                  {stu?.registration_no || "No Reg. No."}
                                </span>
                                {stu?.phone && (
                                  <span>
                                    <i className="ri-phone-line" />
                                    {stu.phone}
                                  </span>
                                )}
                              </small>
                            </span>
                            <span className="scai-result-arrow">
                              <i className="ri-arrow-right-s-line" />
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="scai-search-empty-state">
                        <span>
                          <i className="ri-user-search-line" />
                        </span>
                        <strong>No students found</strong>
                        <small>
                          Try another name, registration number or mobile.
                        </small>
                      </div>
                    )}
                  </div>
                  <div className="scai-search-modal-footer">
                    <span>
                      <kbd>ESC</kbd> to close
                    </span>
                    <span>Student directory</span>
                  </div>
                </section>
              </div>,
              document.body,
            )}

          {/* RIGHT */}
          <div className="scai-topbar-right">
            {setting?.notification_status === "Yes" &&
              ["/notification/send", "/notification/history"].some((route) =>
                hasAccess(route),
              ) && (
                <button
                  type="button"
                  className="scai-topbar-action scai-notification"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <i className="ri-notification-3-line" />
                  <span className="scai-notification-dot" />
                </button>
              )}

            {/* <button
              type="button"
              className="scai-topbar-action"
              onClick={changeTheme}
              title={theme ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Change theme"
            >
              <i className={theme ? "ri-sun-line" : "ri-moon-clear-line"} />
            </button> */}

            <div className="scai-topbar-divider" />

            <div className="scai-action-menu-wrap" ref={actionMenuRef}>
              <button
                type="button"
                className={`scai-topbar-action scai-account-menu-trigger ${showActionMenu ? "is-open" : ""}`}
                onClick={() => setShowActionMenu((open) => !open)}
                title="Account and services"
                aria-label="Account and services"
                aria-haspopup="menu"
                aria-expanded={showActionMenu}
              >
                <i className="ri-more-2-fill" />
              </button>

              {showActionMenu && (
                <div className="scai-action-menu" role="menu">
                  <div className="scai-action-menu-heading">
                    <span className="scai-action-menu-avatar">
                      <i className="ri-user-3-line" />
                    </span>
                    <span>
                      <strong>Quick actions</strong>
                      <small>Services and account</small>
                    </span>
                  </div>

                  {setting?.message_status === "Yes" &&
                    setting?.select_message_type === "Meta" &&
                    [
                      "/meta/wa_send_msg",
                      "/meta/history",
                      "/meta/templates",
                      "/message/list",
                      "/meta/wallet",
                    ].some((route) => hasAccess(route)) && (
                      <button
                        type="button"
                        className="scai-action-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setShowActionMenu(false);
                          handleClick();
                        }}
                      >
                        <span
                          className={`scai-menu-item-icon scai-menu-whatsapp ${messageStatus ? "active" : "inactive"}`}
                        >
                          <i className="ri-whatsapp-line" />
                        </span>
                        <span className="scai-menu-item-copy">
                          <strong>WhatsApp</strong>
                          <small>
                            {messageStatus
                              ? "Service active"
                              : "Service inactive"}
                          </small>
                        </span>
                        <i className="ri-arrow-right-s-line scai-menu-item-arrow" />
                      </button>
                    )}

                  {setting?.message_status === "Yes" &&
                    setting?.select_message_type === "Meta" &&
                    hasAccess("/meta/dashboard") && (
                      <button
                        type="button"
                        className="scai-action-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setShowActionMenu(false);
                          window.open(
                            "https://metawhatsapp.billbazaar.in/",
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        <span className="scai-menu-item-icon scai-menu-whatsapp active">
                          <i className="ri-dashboard-3-line" />
                        </span>

                        <span className="scai-menu-item-copy">
                          <strong>Meta Dashboard</strong>
                          <small>Open Meta Dashboard</small>
                        </span>

                        <i className="ri-arrow-right-s-line scai-menu-item-arrow" />
                      </button>
                    )}

                  <div className="scai-action-menu-separator" />

                  <button
                    type="button"
                    className="scai-action-menu-item scai-action-menu-logout"
                    role="menuitem"
                    onClick={() => {
                      setShowActionMenu(false);
                      logoutUser();
                    }}
                  >
                    <span className="scai-menu-item-icon scai-menu-logout-icon">
                      <i className="ri-logout-box-r-line" />
                    </span>
                    <span className="scai-menu-item-copy">
                      <strong>Logout</strong>
                      <small>Sign out of your account</small>
                    </span>
                    <i className="ri-arrow-right-s-line scai-menu-item-arrow" />
                  </button>
                </div>
              )}
            </div>
            <div
              className="scai-school-info"
              title={setting?.name || "School Management"}
            >
              <div className="scai-school-logo">
                {setting?.logo_img ? (
                  <img
                    src={imgBaseUrl + setting.logo_img}
                    alt={setting?.name || "School"}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <i className="ri-school-line" />
                )}
              </div>

              <div className="scai-school-copy">
                <strong>{setting?.name || "School Management"}</strong>
                <span className="scai-brand-caption">School ERP</span>
              </div>
              {/* <span
                className="scai-session-pill"
                title={
                  activeSession?.session
                    ? `Active session: ${activeSession.session}`
                    : "No active session"
                }
              >
                <span className="scai-session-indicator" />
                <span>{activeSession?.session || "No session"}</span>
              </span> */}
            </div>
          </div>
        </div>
      </nav>

      <style>{`
        .scai-config-tabs-shell {
          position: relative;
          z-index: 1040;
          margin: 0;
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 10px;
          padding: 0;
          border: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
        }
        .scai-config-tabs-brand { display:flex; align-items:center; gap:8px; color:var(--bs-body-color,#344054); flex-shrink:0; padding:0 10px 0 3px; border-right:1px solid var(--bs-border-color,#e5e7eb); }
        .scai-config-tabs-brand i {font-size:19px;color:var(--bs-primary,#696cff)}
        .scai-config-tabs-brand strong {font-size:12px;white-space:nowrap}
        .scai-config-tabs-list { min-width:0; display:flex; align-items:center; gap:5px; flex:1; overflow-x:auto; overflow-y:visible; scrollbar-width:thin; padding:3px 2px; }
        .scai-config-tab {display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid transparent;background:transparent;color:var(--bs-secondary-color,#667085);border-radius:10px;padding:10px 12px;min-height:41px;white-space:nowrap;font-size:12px;font-weight:650;text-decoration:none!important;cursor:pointer}
        .scai-config-tab i {font-size:17px}
        .scai-config-tab:hover {background:rgba(105,108,255,.08);color:var(--bs-primary,#696cff)}
        .scai-config-tab.active {background:var(--bs-primary,#696cff);color:white!important;box-shadow:0 3px 9px rgba(105,108,255,.19)}
        .scai-config-tabs-dropdown {position:static}
        .scai-config-tab-popover {position:absolute;right:12px;top:calc(100% - 2px);z-index:1090;width:min(470px,calc(100vw - 40px));max-height:55vh;overflow-y:auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;padding:12px;border:1px solid var(--bs-border-color,#e5e7eb);border-radius:13px;background:var(--bs-card-bg,#fff);box-shadow:0 14px 42px rgba(15,23,42,.20)}
        .scai-config-tab-option {display:flex;gap:10px;align-items:center;min-width:0;padding:10px;border:1px solid var(--bs-border-color,#e5e7eb);border-radius:10px;text-decoration:none!important;color:var(--bs-body-color,#344054)}
        .scai-config-tab-option:hover,.scai-config-tab-option.active {background:rgba(105,108,255,.08);color:var(--bs-primary,#696cff)}
        .scai-config-tab-option>i {font-size:20px;color:var(--bs-primary,#696cff)}
        .scai-config-tab-option span {min-width:0;display:flex;flex-direction:column;gap:2px}
        .scai-config-tab-option strong {font-size:12px}
        .scai-config-tab-option small {font-size:10px;opacity:.7}
        [data-style="dark"] .scai-config-tabs-shell, [data-style="dark"] .scai-config-tab-popover {background:#2b2c40;color:#e6e7f0;border-color:#474961}
        [data-style="dark"] .scai-config-tab-option {color:#e6e7f0;border-color:#474961}
        @media(max-width:767px) { .scai-config-tabs-shell {margin:0; padding:0;gap:5px} .scai-config-tabs-brand strong {display:none} .scai-config-tab {padding:8px 10px;min-height:37px} .scai-config-tab-popover {grid-template-columns:1fr;right:5px} }
        @media print { .scai-config-tabs-shell {display:none!important} }

        /* =========================================================
           SCAI TOP NAVBAR
        ========================================================= */
        .scai-topbar {
          --topbar-primary: #696cff;
          --topbar-primary-soft: rgba(105, 108, 255, 0.08);
          --topbar-text: #344054;
          --topbar-title: #101828;
          --topbar-muted: #98a2b3;
          --topbar-border: #eaecf0;
          --topbar-surface: rgba(255, 255, 255, 0.97);
          --topbar-search-surface: rgba(152, 162, 179, 0.075);
          --topbar-logo-surface: rgba(105, 108, 255, 0.07);
          --topbar-session-surface: rgba(105, 108, 255, 0.055);

          position: sticky !important;
          top: 0 !important;
          left: auto !important;
          width: 100% !important;
          max-width: none !important;
          min-height: 64px;
          margin: 0 !important;
          padding: 0 !important;
          border: 1px solid var(--topbar-border) !important;
          border-radius: 0 !important;
          background: var(--topbar-surface) !important;
          box-shadow: 0 7px 25px rgba(16, 24, 40, 0.055) !important;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          z-index: 1050;
        }

        .scai-topbar-inner {
          width: 100%;
          min-width: 0;
          min-height: 64px;
          padding: 7px 10px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          align-items: center;
          gap: 14px;
        }

        .scai-global-search-launcher {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .scai-search-launcher {
          width: 40px;
          height: 40px;
          flex-basis: 40px;
          border-radius: 12px;
          color: var(--topbar-title);
          background: var(--topbar-search-surface, rgba(152, 162, 179, 0.075));
        }

        .scai-search-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 5000;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: min(12vh, 100px) 18px 24px;
          background: rgba(16, 24, 40, 0.48);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .scai-search-modal {
          width: min(620px, 100%);
          max-height: min(680px, 78vh);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid var(--topbar-border, #eaecf0);
          border-radius: 18px;
          background: var(--topbar-surface, #fff);
          box-shadow: 0 24px 80px rgba(16, 24, 40, 0.24);
          color: var(--topbar-title, #101828);
        }

        .scai-search-modal-heading {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 20px;
          border-bottom: 1px solid var(--topbar-border, #eaecf0);
        }

        .scai-search-modal-symbol {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: grid;
          place-items: center;
          border-radius: 13px;
          background: var(--topbar-primary-soft, rgba(105, 108, 255, .08));
          color: var(--topbar-primary, #696cff);
          font-size: 21px;
        }

        .scai-search-modal-heading > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .scai-search-modal-heading strong { font-size: 15px; font-weight: 800; color: var(--topbar-title, #101828); }
        .scai-search-modal-heading small { font-size: 11px; color: var(--topbar-muted, #98a2b3); }

        .scai-search-modal-close {
          width: 34px;
          height: 34px;
          margin-left: auto;
          flex: 0 0 34px;
          display: grid;
          place-items: center;
          border: 1px solid var(--topbar-border, #eaecf0);
          border-radius: 10px;
          background: transparent;
          color: var(--topbar-muted, #98a2b3);
          cursor: pointer;
          font-size: 18px;
        }

        .scai-search-modal-input-wrap {
          margin: 16px 20px 12px;
          min-height: 48px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 13px;
          border: 1px solid var(--topbar-border, #eaecf0);
          border-radius: 12px;
          background: var(--topbar-search-surface, rgba(152, 162, 179, .075));
          color: var(--topbar-muted, #98a2b3);
        }

        .scai-search-modal-input-wrap:focus-within {
          border-color: rgba(105, 108, 255, .62);
          box-shadow: 0 0 0 3px rgba(105, 108, 255, .1);
        }

        .scai-search-modal-input-wrap > i { font-size: 19px; flex: 0 0 auto; }
        .scai-search-modal-input-wrap input {
          width: 100%;
          min-width: 0;
          height: 46px;
          padding: 0;
          border: 0;
          outline: 0;
          background: transparent;
          color: var(--topbar-title, #101828);
          font-size: 13px;
        }
        .scai-search-modal-input-wrap input::placeholder { color: var(--topbar-muted, #98a2b3); }
        .scai-search-modal-input-wrap button {
          display: grid;
          place-items: center;
          padding: 4px;
          border: 0;
          background: transparent;
          color: var(--topbar-muted, #98a2b3);
          font-size: 18px;
          cursor: pointer;
        }

        .scai-search-modal-results-label {
          padding: 4px 20px 8px;
          color: var(--topbar-muted, #98a2b3);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .08em;
        }

        .scai-search-modal-results {
          min-height: 170px;
          overflow-y: auto;
          padding: 0 12px 12px;
        }

        .scai-search-modal-results .scai-student-result { color: var(--topbar-title, #101828); }
        .scai-search-modal-results .scai-student-result:hover { background: var(--topbar-primary-soft, rgba(105, 108, 255, .08)); }
        .scai-search-modal-results .scai-student-data strong { color: var(--topbar-title, #101828); }
        .scai-search-modal-results .scai-student-data small { color: var(--topbar-muted, #98a2b3); }

        .scai-search-empty-state {
          min-height: 190px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 20px;
          text-align: center;
        }
        .scai-search-empty-state > span {
          width: 48px;
          height: 48px;
          display: grid;
          place-items: center;
          border-radius: 15px;
          background: var(--topbar-primary-soft, rgba(105, 108, 255, .08));
          color: var(--topbar-primary, #696cff);
          font-size: 23px;
        }
        .scai-search-empty-state strong { color: var(--topbar-title, #101828); font-size: 12px; }
        .scai-search-empty-state small { max-width: 320px; color: var(--topbar-muted, #98a2b3); font-size: 10px; line-height: 1.6; }

        .scai-search-modal-footer {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 11px 20px;
          border-top: 1px solid var(--topbar-border, #eaecf0);
          color: var(--topbar-muted, #98a2b3);
          font-size: 10px;
        }
        .scai-search-modal-footer kbd {
          padding: 2px 5px;
          border: 1px solid var(--topbar-border, #eaecf0);
          border-radius: 5px;
          font: inherit;
        }

        /* LEFT */
        .scai-topbar-left {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .scai-topbar-action {
          width: 36px;
          height: 36px;
          flex: 0 0 36px;
          padding: 0;
          position: relative;
          display: grid;
          place-items: center;
          border: 1px solid #e4e7ec;
          border-radius: 11px;
          outline: none;
          background: #fff;
          color: #667085;
          cursor: pointer;
          transition:
            color 0.16s ease,
            border-color 0.16s ease,
            background 0.16s ease,
            transform 0.16s ease,
            box-shadow 0.16s ease;
        }

        .scai-topbar-action i {
          font-size: 18px;
          line-height: 1;
        }

        .scai-topbar-action:hover {
          color: var(--topbar-primary);
          border-color: rgba(105, 108, 255, 0.3);
          background: var(--topbar-primary-soft);
          box-shadow: 0 4px 10px rgba(105, 108, 255, 0.08);
          transform: translateY(-1px);
        }

        .scai-back-action {
          background: #f9fafb;
        }

        .scai-school-info {
          min-width: 0;
          max-width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 3px 4px 3px 0;
        }

        .scai-school-logo {
          width: 43px;
          height: 43px;
          flex: 0 0 43px;
          display: grid;
          place-items: center;
          overflow: hidden;
          border: 1px solid var(--topbar-border);
          border-radius: 14px;
          background: var(--topbar-logo-surface, rgba(105, 108, 255, 0.07));
          color: var(--topbar-primary);
        }

        .scai-school-logo img {
          width: 100%;
          height: 100%;
          padding: 4px;
          object-fit: contain;
        }

        .scai-school-logo > i { font-size: 22px; }

        .scai-school-copy {
          min-width: 0;
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          line-height: 1.2;
        }

        .scai-school-copy strong {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--topbar-title);
          font-size: 13px;
          font-weight: 800;
          letter-spacing: -0.025em;
        }

        .scai-brand-caption {
          margin-top: 4px;
          color: var(--topbar-muted);
          font-size: 9px;
          font-weight: 650;
          letter-spacing: 0.045em;
          text-transform: uppercase;
        }

        .scai-session-pill {
          flex: 0 0 auto;
          max-width: 130px;
          min-height: 29px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0 8px;
          border: 1px solid var(--topbar-border);
          border-radius: 9px;
          background: var(--topbar-session-surface, rgba(105, 108, 255, 0.055));
          color: var(--topbar-title);
          font-size: 9px;
          font-weight: 750;
          white-space: nowrap;
        }

        .scai-session-pill > span:nth-child(2) {
          max-width: 76px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .scai-session-pill > i { color: var(--topbar-muted); font-size: 13px; }

        .scai-session-indicator {
          width: 6px;
          height: 6px;
          flex: 0 0 6px;
          border-radius: 50%;
          background: #12b76a;
          box-shadow: 0 0 0 3px rgba(18, 183, 106, 0.11);
        }

        /* SEARCH */
        .scai-global-search {
          width: 100%;
          max-width: 680px;
          min-width: 0;
          position: relative;
          justify-self: center;
        }

        .scai-search-box {
          width: 100%;
          min-width: 0;
          height: 40px;
          position: relative;
          display: flex;
          align-items: center;
        }

        .scai-search-box input {
          width: 100%;
          height: 44px;
          padding: 0 42px 0 115px;
          border: 1px solid var(--topbar-border);
          border-radius: 13px;
          outline: none;
          background: var(--topbar-search-surface, rgba(152, 162, 179, 0.075));
          color: var(--topbar-title);
          font-size: 11px;
          font-weight: 550;
          transition:
            border-color 0.18s ease,
            background 0.18s ease,
            box-shadow 0.18s ease;
        }

        .scai-search-box input::placeholder {
          color: #98a2b3;
        }

        .scai-search-box input:focus {
          border-color: rgba(105, 108, 255, 0.48);
          background: var(--topbar-surface);
          box-shadow: 0 0 0 3px rgba(105, 108, 255, 0.09);
        }

        .scai-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 2;
          transform: translateY(-50%);
          color: #98a2b3;
          font-size: 17px;
          pointer-events: none;
        }

        .scai-search-scope {
          position: absolute;
          left: 38px;
          top: 50%;
          z-index: 2;
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 6px 9px;
          border-right: 1px solid var(--topbar-border);
          color: var(--topbar-primary);
          font-size: 10px;
          font-weight: 800;
          pointer-events: none;
        }

        .scai-search-enter {
          position: absolute;
          right: 11px;
          top: 50%;
          transform: translateY(-50%);
          display: grid;
          place-items: center;
          width: 23px;
          height: 23px;
          border: 1px solid var(--topbar-border);
          border-radius: 7px;
          color: var(--topbar-muted);
          font-size: 12px;
          pointer-events: none;
        }

        .scai-search-hint {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          padding: 4px 7px;
          border: 1px solid var(--topbar-border);
          border-radius: 6px;
          background: transparent;
          color: var(--topbar-muted);
          font-size: 8px;
          font-weight: 700;
          pointer-events: none;
        }

        .scai-search-clear {
          position: absolute;
          right: 8px;
          top: 50%;
          width: 28px;
          height: 28px;
          padding: 0;
          transform: translateY(-50%);
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: #98a2b3;
          cursor: pointer;
        }

        .scai-search-clear:hover {
          background: #f2f4f7;
          color: #475467;
        }

        .scai-search-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          overflow: hidden;
          border: 1px solid #eaecf0;
          border-radius: 14px;
          background: #fff;
          box-shadow: 0 18px 40px rgba(16, 24, 40, 0.14);
          z-index: 3000;
        }

        .scai-search-dropdown-head {
          min-height: 48px;
          padding: 9px 11px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          border-bottom: 1px solid #f0f1f3;
          background: #fcfcfd;
        }

        .scai-search-dropdown-head > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .scai-search-dropdown-head strong {
          color: #344054;
          font-size: 11px;
          font-weight: 800;
        }

        .scai-search-dropdown-head span {
          margin-top: 2px;
          color: #98a2b3;
          font-size: 8px;
        }

        .scai-search-dropdown-head button {
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: #98a2b3;
          cursor: pointer;
        }

        .scai-search-dropdown-head button:hover {
          background: #f2f4f7;
          color: #475467;
        }

        .scai-search-dropdown-body {
          max-height: 350px;
          overflow-y: auto;
          padding: 6px;
        }

        .scai-student-result {
          width: 100%;
          min-height: 54px;
          padding: 7px 8px;
          display: flex;
          align-items: center;
          gap: 9px;
          border: 0;
          border-radius: 10px;
          background: transparent;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .scai-student-result:hover {
          background: #f8f8fc;
        }

        .scai-student-avatar {
          width: 36px;
          height: 36px;
          flex: 0 0 36px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          background: rgba(105, 108, 255, 0.09);
          color: var(--topbar-primary);
          font-size: 10px;
          font-weight: 850;
        }

        .scai-student-data {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .scai-student-data > strong {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #344054;
          font-size: 11px;
          font-weight: 750;
        }

        .scai-student-data small {
          margin-top: 4px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          color: #98a2b3;
          font-size: 8px;
        }

        .scai-student-data small span {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .scai-result-arrow {
          width: 27px;
          height: 27px;
          flex: 0 0 27px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          color: #b2b7c0;
        }

        .scai-student-result:hover .scai-result-arrow {
          color: var(--topbar-primary);
          background: rgba(105, 108, 255, 0.07);
        }

        .scai-no-results {
          min-height: 130px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .scai-no-results > span {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          background: #f2f4f7;
          color: #98a2b3;
          font-size: 20px;
        }

        .scai-no-results strong {
          margin-top: 8px;
          color: #475467;
          font-size: 11px;
        }

        .scai-no-results small {
          margin-top: 3px;
          color: #98a2b3;
          font-size: 8px;
        }

        /* RIGHT */
        .scai-topbar-right {
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
        }

        .scai-date-time {
          min-height: 42px;
          padding: 5px 9px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid var(--topbar-border);
          border-radius: 12px;
          background: var(--topbar-search-surface, rgba(152, 162, 179, 0.075));
          white-space: nowrap;
        }

        .scai-date-time-icon {
          width: 30px;
          height: 30px;
          flex: 0 0 30px;
          display: grid;
          place-items: center;
          border-radius: 9px;
          background: var(--topbar-primary-soft);
          color: var(--topbar-primary);
        }

        .scai-date-time-icon i { font-size: 15px; }

        .scai-clock-copy {
          display: flex;
          flex-direction: column;
          line-height: 1.1;
        }

        .scai-date-time strong {
          color: var(--topbar-title);
          font-size: 11px;
          font-weight: 800;
          font-variant-numeric: tabular-nums;
        }

        .scai-date-time small {
          margin-top: 4px;
          color: var(--topbar-muted);
          font-size: 8px;
          font-weight: 650;
        }

        .scai-whatsapp.active {
          color: #079455;
          border-color: #abefc6;
          background: #ecfdf3;
        }

        .scai-whatsapp.inactive {
          color: #d92d20;
          border-color: #fecdca;
          background: #fef3f2;
        }

        .scai-action-status {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 6px;
          height: 6px;
          border: 1px solid #fff;
          border-radius: 50%;
          background: currentColor;
        }

        .scai-notification-dot {
          position: absolute;
          top: 6px;
          right: 7px;
          width: 5px;
          height: 5px;
          border: 1px solid #fff;
          border-radius: 50%;
          background: #f04438;
        }

        .scai-topbar-divider {
          width: 1px;
          height: 25px;
          margin: 0 1px;
          background: #eaecf0;
        }

        .scai-logout-action {
          color: #d92d20;
          border-color: #fee4e2;
          background: #fff8f7;
        }

        .scai-logout-action:hover {
          color: #b42318;
          border-color: #fecdca;
          background: #fff1f0;
        }

        /* ACCOUNT / QUICK ACTIONS MENU */
        .scai-action-menu-wrap {
          position: relative;
          flex: 0 0 auto;
        }

        .scai-account-menu-trigger {
          color: var(--topbar-title);
          background: var(--topbar-primary-soft);
          border-color: rgba(105, 108, 255, 0.2);
        }

        .scai-account-menu-trigger.is-open {
          color: var(--topbar-primary);
          border-color: rgba(105, 108, 255, 0.42);
          box-shadow: 0 0 0 3px rgba(105, 108, 255, 0.09);
        }

        .scai-action-menu {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          z-index: 4000;
          width: 248px;
          padding: 7px;
          border: 1px solid var(--topbar-border);
          border-radius: 15px;
          background: var(--topbar-surface);
          box-shadow: 0 18px 45px rgba(16, 24, 40, 0.16);
          animation: scai-menu-in 0.14s ease-out;
        }

        @keyframes scai-menu-in {
          from { opacity: 0; transform: translateY(-4px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .scai-action-menu-heading {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 8px 12px;
          margin-bottom: 4px;
          border-bottom: 1px solid var(--topbar-border);
        }

        .scai-action-menu-avatar {
          width: 36px;
          height: 36px;
          flex: 0 0 36px;
          display: grid;
          place-items: center;
          border-radius: 11px;
          color: var(--topbar-primary);
          background: var(--topbar-primary-soft);
          font-size: 17px;
        }

        .scai-action-menu-heading > span:last-child,
        .scai-menu-item-copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .scai-action-menu-heading strong,
        .scai-menu-item-copy strong {
          color: var(--topbar-title);
          font-size: 11px;
          font-weight: 750;
          line-height: 1.35;
        }

        .scai-action-menu-heading small,
        .scai-menu-item-copy small {
          margin-top: 3px;
          color: var(--topbar-muted);
          font-size: 9px;
          line-height: 1.35;
        }

        .scai-action-menu-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 8px;
          border: 0;
          border-radius: 10px;
          background: transparent;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .scai-action-menu-item:hover {
          background: var(--topbar-primary-soft);
        }

        .scai-menu-item-icon {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          font-size: 17px;
        }

        .scai-menu-whatsapp.active {
          color: #079455;
          background: rgba(18, 183, 106, 0.12);
        }

        .scai-menu-whatsapp.inactive {
          color: #d92d20;
          background: rgba(240, 68, 56, 0.1);
        }

        .scai-menu-logout-icon {
          color: #d92d20;
          background: rgba(240, 68, 56, 0.1);
        }

        .scai-menu-item-copy { flex: 1; }
        .scai-menu-item-arrow { color: var(--topbar-muted); font-size: 17px; }

        .scai-action-menu-separator {
          height: 1px;
          margin: 5px 7px;
          background: var(--topbar-border);
        }

        .scai-action-menu-logout:hover {
          background: rgba(240, 68, 56, 0.07);
        }

        html[data-style="dark"] .scai-action-menu {
          border-color: rgba(255, 255, 255, 0.09);
          background: #2b2c40;
        }

        html[data-style="dark"] .scai-action-menu-heading strong,
        html[data-style="dark"] .scai-menu-item-copy strong {
          color: #f2f4f7;
        }

        html[data-style="dark"] .scai-action-menu-item:hover {
          background: rgba(255, 255, 255, 0.055);
        }

        /* DARK MODE */
        html[data-style="dark"] .scai-topbar {
          --topbar-text: #d0d5dd;
          --topbar-title: #f2f4f7;
          --topbar-muted: #98a2b3;
          --topbar-border: rgba(255, 255, 255, 0.08);
          --topbar-surface: rgba(43, 44, 64, 0.97);
          --topbar-search-surface: rgba(255, 255, 255, 0.035);
          --topbar-logo-surface: rgba(105, 108, 255, 0.12);
          --topbar-session-surface: rgba(105, 108, 255, 0.09);
        }

        html[data-style="dark"] .scai-topbar-action,
        html[data-style="dark"] .scai-search-box input,
        html[data-style="dark"] .scai-date-time {
          border-color: rgba(255, 255, 255, 0.08);
          background: #303149;
          color: #d0d5dd;
        }

        html[data-style="dark"] .scai-school-logo {
          border-color: rgba(255, 255, 255, 0.08);
          background: var(--topbar-logo-surface);
        }

        html[data-style="dark"] .scai-search-scope {
          border-color: rgba(255, 255, 255, 0.1);
        }

        html[data-style="dark"] .scai-search-enter {
          border-color: rgba(255, 255, 255, 0.1);
        }

        html[data-style="dark"] .scai-school-copy strong,
        html[data-style="dark"] .scai-date-time strong {
          color: #f2f4f7;
        }

        html[data-style="dark"] .scai-search-box input {
          color: #eaecf0;
        }

        html[data-style="dark"] .scai-search-box input:focus {
          background: #34354d;
        }

        html[data-style="dark"] .scai-search-dropdown {
          border-color: rgba(255, 255, 255, 0.08);
          background: #2b2c40;
        }

        html[data-style="dark"] .scai-search-dropdown-head {
          border-color: rgba(255, 255, 255, 0.08);
          background: #303149;
        }

        html[data-style="dark"] .scai-search-dropdown-head strong,
        html[data-style="dark"] .scai-student-data > strong {
          color: #eaecf0;
        }

        html[data-style="dark"] .scai-student-result:hover {
          background: #34354d;
        }

        /* RESPONSIVE */
        @media (min-width: 1600px) {
          .scai-topbar-inner {
            grid-template-columns: minmax(0, 1fr) auto auto;
          }
        }

        @media (max-width: 1450px) {
          .scai-topbar {
            margin-left: 16px !important;
            margin-right: 16px !important;
          }

          .scai-topbar-inner {
            grid-template-columns: minmax(0, 1fr) auto auto;
            gap: 10px;
          }
          .scai-school-copy { max-width: 150px; }
          .scai-session-pill { max-width: 105px; }
          .scai-date-time { padding: 5px 7px; }
          .scai-date-time-icon { display: none; }
        }

        @media (max-width: 1199.98px) {
          .scai-topbar {
            width: auto;
            margin: 10px 12px 0 !important;
          }

          .scai-topbar-inner {
            grid-template-columns: minmax(0, 1fr) auto auto;
            padding: 7px 9px;
          }

          .scai-topbar-inner {
            grid-template-columns: minmax(0, 1fr) auto auto;
          }
          .scai-school-info { gap: 0; }
          .scai-school-logo { width: 38px; height: 38px; flex-basis: 38px; }
          .scai-school-copy, .scai-session-pill { display: none; }
          .scai-back-action { display: none; }
          .scai-date-time { display: none; }
        }

        @media (max-width: 767.98px) {
          .scai-topbar {
            min-height: 62px;
            border-radius: 13px !important;
          }

          .scai-topbar-inner {
            min-height: 62px;
            grid-template-columns: auto minmax(0, 1fr) auto;
            gap: 6px;
            padding: 6px 8px;
          }

          .scai-back-action {
            display: none;
          }


          .scai-topbar-right {
            gap: 4px;
          }

          .scai-topbar-right .scai-topbar-action {
            width: 35px;
            height: 35px;
            flex-basis: 35px;
          }

          .scai-topbar-divider {
            display: none;
          }

          .scai-action-menu {
            right: 0;
            width: min(248px, calc(100vw - 24px));
          }
        }

        @media (max-width: 520px) {
          .scai-topbar {
            width: auto;
            margin: 6px 6px 0 !important;
          }

          .scai-topbar-inner {
            gap: 5px;
            padding: 6px;
          }

          .scai-mobile-menu {
            width: 34px;
            height: 34px;
            flex-basis: 34px;
          }

          .scai-topbar-right .scai-topbar-action {
            width: 33px;
            height: 33px;
            flex-basis: 33px;
          }

          .scai-notification {
            display: none;
          }

        }

        @media (max-width: 390px) {
          .scai-topbar-right > .scai-topbar-action:not(.scai-account-menu-trigger) {
            display: none;
          }

          .scai-topbar-inner {
            grid-template-columns: auto minmax(0, 1fr) auto;
          }
        }

        @media (max-width: 520px) {
          .scai-search-modal-backdrop { padding: 8vh 10px 16px; }
          .scai-search-modal { max-height: 84vh; border-radius: 15px; }
          .scai-search-modal-heading { padding: 14px; gap: 9px; }
          .scai-search-modal-heading small { max-width: 220px; line-height: 1.4; }
          .scai-search-modal-input-wrap { margin: 12px 14px 10px; }
          .scai-search-modal-results-label { padding-left: 14px; }
          .scai-search-modal-results { padding-right: 7px; padding-left: 7px; }
          .scai-search-modal-footer { padding: 10px 14px; }
        }

        @media print {
          .scai-topbar {
            display: none !important;
          }
        }

        /* ===== 2026 SINGLE-ROW TOPBAR: MENU LEFT, SCHOOL RIGHT ===== */
        .scai-topbar { margin: 10px 18px 12px !important; min-height: 68px; overflow: visible !important; }
        .scai-topbar-inner { display: flex !important; flex-wrap: nowrap; gap: 12px; min-height: 68px; padding: 8px 12px; }
        .scai-topbar-left { flex: 1 1 auto; min-width: 0; gap: 10px; overflow: visible; }
        .scai-topbar-right { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; min-width: 0; margin-left: 0; }
        .scai-global-search-launcher { flex: 0 0 auto; order: 2; margin-left: auto; }
        .scai-topbar-right { order: 3; }
        .scai-school-info { display: flex; flex-direction: row; align-items: center; min-width: 0; max-width: 350px; padding: 0 0 0 12px; border-left: 1px solid var(--topbar-border); gap: 9px; }
        .scai-school-logo { flex-shrink: 0; }
        .scai-school-copy { min-width: 0; }
        .scai-school-copy strong { display: block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 260px; }
        .scai-config-tabs-shell { display: flex; flex: 1 1 auto; min-width: 0; min-height: 46px; position: relative; z-index: 1; margin: 0; padding: 0; gap: 9px; box-shadow: none; border: 0; background: transparent; }
        .scai-config-tabs-brand { padding: 0 12px 0 4px; gap: 7px; }
        .scai-config-tabs-brand strong { font-size: 12px; }
        .scai-config-tabs-list { min-width: 0; gap: 3px; padding: 3px 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: none; scroll-behavior: smooth; }
        .scai-config-tabs-list::-webkit-scrollbar { display: none; }
        .scai-config-tab { min-height: 40px; padding: 9px 11px; font-size: 12px; border-radius: 10px; }
        .scai-config-tab.active { background: linear-gradient(135deg, #6578ed, #7954f8); }
        .scai-config-tab-popover { top: calc(100% + 8px); right: 10px; z-index: 9999; }
        .scai-config-tabs-dropdown { position: static; }
        .scai-topbar-right .scai-topbar-divider { display: none; }
        html[data-style="dark"] .scai-config-tabs-shell { background: transparent; border: 0; }
        /* Config-only module tabs: integrated in main header, no second bar */
        .scai-config-tabs-shell {max-width:100%;flex:1 1 auto;margin:0!important;padding:0!important;min-width:0;border:0!important;background:transparent!important;box-shadow:none!important}
        .scai-config-tabs-brand {max-width:155px;overflow:hidden}
        .scai-config-tabs-list {scrollbar-width:none;overflow-x:auto!important;overflow-y:hidden!important}
        .scai-config-tabs-list::-webkit-scrollbar {display:none}
        .scai-config-tab {padding:8px 10px;min-height:38px}
        .scai-topbar-left {min-width:0;overflow:hidden!important}
        .scai-config-tab-popover {z-index:6000!important}
        @media(max-width: 1550px) {
          .scai-school-info { max-width: 260px; }
          .scai-school-copy strong { max-width: 175px; }
          .scai-config-tabs-brand strong { display: none; }
        }
        @media(max-width: 1150px) {
          .scai-topbar { margin: 8px 12px 10px !important; }
          .scai-topbar-inner { gap: 7px; }
          .scai-school-info { max-width: 205px; padding-left: 8px; }
          .scai-school-copy strong { max-width: 132px; font-size: 11px; }
          .scai-config-tab { padding: 8px 9px; }
        }
        @media(max-width: 767px) {
          .scai-topbar-inner { padding: 7px 8px; }
          .scai-topbar-left { gap: 5px; }
          .scai-config-tabs-brand { display: none; }
          .scai-config-tabs-shell { min-width: 0; gap: 0; min-height: 42px; }
          .scai-config-tab { min-height: 37px; font-size: 11px; padding: 7px 9px; }
          .scai-school-info { border: none; max-width: 42px; padding: 0; }
          .scai-school-copy { display: none; }
          .scai-school-logo { width: 36px; height: 36px; }
          .scai-global-search-launcher { margin-left: 0; }
          .scai-config-tab-popover { position: fixed; left: 10px; right: 10px; top: 75px; width: auto; grid-template-columns: 1fr; }
        }
        @media(max-width: 420px) {
          .scai-topbar-inner { gap: 4px; }
          .scai-topbar-right { gap: 4px; }
          .scai-school-info { max-width: 30px; }
          .scai-school-logo { width: 30px; height: 30px; }
          .scai-config-tab span { display: none; }
        }


        /* =============================================================
           SCAI EDGE-TO-EDGE TOP NAVBAR (final layout overrides)
           This block intentionally comes last to override old theme rules.
           ============================================================= */
        :root { --scai-navbar-height: 66px; }
        .layout-navbar-fixed .layout-wrapper:not(.layout-without-menu)
        .layout-page:has(> #layout-navbar.scai-topbar) {
          padding-top: 0 !important;
        }
        #layout-navbar.scai-topbar {
          --topbar-surface: var(--bs-card-bg, #ffffff);
          --topbar-border: var(--bs-border-color, #e7e9f1);
          --topbar-text: var(--bs-body-color, #344054);
          --topbar-title: var(--bs-heading-color, #25293c);
          --topbar-muted: var(--bs-secondary-color, #818a9f);
          position: sticky !important;
          top: 0 !important;
          right: auto !important;
          left: auto !important;
          transform: none !important;
          box-sizing: border-box !important;
          display: block !important;
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          min-height: var(--scai-navbar-height) !important;
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          border-bottom: 1px solid var(--topbar-border) !important;
          border-radius: 0 !important;
          background: var(--topbar-surface) !important;
          box-shadow: 0 2px 11px rgba(30, 39, 65, .035) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          overflow: visible !important;
          z-index: 1075;
        }
        #layout-navbar.scai-topbar .scai-topbar-inner {
          box-sizing: border-box;
          width: 100%;
          min-height: var(--scai-navbar-height);
          margin: 0;
          padding: 8px clamp(10px, 1.4vw, 24px);
          display: flex !important;
          flex-wrap: nowrap;
          align-items: center;
          gap: 11px;
        }
        #layout-navbar.scai-topbar .scai-topbar-left {
          display: flex;
          align-items: center;
          flex: 1 1 0%;
          min-width: 0;
          overflow: hidden !important;
          gap: 9px;
        }
        #layout-navbar.scai-topbar .scai-config-tabs-shell {
          flex: 1 1 0%;
          width: 100%;
          min-width: 0;
          max-width: 100%;
          border: 0 !important;
          background: transparent !important;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
        }
        #layout-navbar.scai-topbar .scai-config-tabs-list {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: none;
          min-width: 0;
        }
        #layout-navbar.scai-topbar .scai-config-tabs-list::-webkit-scrollbar { display: none; }
        #layout-navbar.scai-topbar .scai-config-tabs-brand {
          border-color: var(--topbar-border);
          color: var(--topbar-title);
        }
        #layout-navbar.scai-topbar .scai-config-tab {
          border-radius: 9px;
          color: var(--topbar-muted);
          min-height: 39px;
          padding: 8px 11px;
          font-weight: 700;
        }
        #layout-navbar.scai-topbar .scai-config-tab.active {
          background: #696cff;
          color: #fff !important;
          box-shadow: 0 2px 8px rgba(105,108,255,.18);
        }
        #layout-navbar.scai-topbar .scai-topbar-action {
          color: var(--topbar-text);
          background: var(--topbar-surface);
          border-color: var(--topbar-border);
          box-shadow: none;
        }
        #layout-navbar.scai-topbar .scai-topbar-action:hover {
          color: #696cff;
          background: rgba(105,108,255,.08);
          border-color: rgba(105,108,255,.27);
        }
        #layout-navbar.scai-topbar .scai-topbar-right {
          flex: 0 0 auto;
          order: 3;
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        #layout-navbar.scai-topbar .scai-global-search-launcher {
          flex: 0 0 auto;
          order: 2;
          margin-left: 0;
        }
        #layout-navbar.scai-topbar .scai-school-info {
          flex: 0 1 auto;
          min-width: 0;
          max-width: 290px;
          padding-left: 11px;
          gap: 9px;
          border-left: 1px solid var(--topbar-border);
        }
        #layout-navbar.scai-topbar .scai-school-copy strong { max-width: 225px; }
        #layout-navbar.scai-topbar .scai-school-logo {
          background: rgba(105,108,255,.07);
          border-color: var(--topbar-border);
        }
        html[data-style="dark"] #layout-navbar.scai-topbar {
          --topbar-surface: #2b2c40;
          --topbar-border: #484963;
          --topbar-text: #d5d6ec;
          --topbar-title: #f0f0f8;
          --topbar-muted: #b5b7d1;
          box-shadow: 0 2px 12px rgba(0,0,0,.17) !important;
        }
        html[data-style="dark"] #layout-navbar.scai-topbar .scai-topbar-action {
          background: #32334b;
          color: #d5d6ec;
        }
        @media (max-width: 1280px) {
          #layout-navbar.scai-topbar .scai-config-tabs-brand strong { display: none; }
          #layout-navbar.scai-topbar .scai-school-info { max-width: 205px; }
          #layout-navbar.scai-topbar .scai-school-copy strong { max-width: 140px; }
        }
        @media (max-width: 991.98px) {
          :root { --scai-navbar-height: 60px; }
          #layout-navbar.scai-topbar .scai-topbar-inner { gap: 7px; padding: 7px 10px; }
          #layout-navbar.scai-topbar .scai-config-tabs-brand { display: none; }
          #layout-navbar.scai-topbar .scai-school-info { max-width: 44px; padding: 0; border: 0; }
          #layout-navbar.scai-topbar .scai-school-copy { display: none; }
        }
        @media (max-width: 575.98px) {
          #layout-navbar.scai-topbar .scai-topbar-inner { gap: 5px; padding: 6px 7px; }
          #layout-navbar.scai-topbar .scai-topbar-left { gap: 5px; }
          #layout-navbar.scai-topbar .scai-config-tab { padding: 6px 8px; }
          #layout-navbar.scai-topbar .scai-topbar-right { gap: 4px; }
          #layout-navbar.scai-topbar .scai-school-info { max-width: 34px; }
          #layout-navbar.scai-topbar .scai-school-logo { width: 33px; height: 33px; flex-basis: 33px; }
        }
        @media print { #layout-navbar.scai-topbar { display: none !important; } }
      `}</style>
    </>
  );
}
